import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { StatusBadge, whatsappLink, fmtDate } from "./shared";
import { MatchPanel } from "./MatchPanel";
import { ShareComposer } from "./ShareComposer";

const ROWS = [
  ["purpose", "Purpose"], ["city", "City"], ["area", "Area"], ["property_type", "Type"], ["configuration", "Configuration"],
  ["budget", "Budget"], ["possession", "Possession"], ["priorities", "Priorities"], ["visit_timing", "India visit"],
];

const Notes = ({ lead, onAdded }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/leads/${lead.id}/notes`, { text });
      setText("");
      onAdded();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };
  return (
    <div className="mt-6">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">Chat notes</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} placeholder="What did you discuss?" className="mt-2 w-full rounded-xl border border-neutral-200 p-3 text-sm outline-none focus:border-[var(--saffron)]" data-testid="lead-note-input" />
      <button onClick={add} disabled={busy || !text.trim()} className="btn-saffron mt-2 h-10 rounded-full px-5 text-sm font-semibold" data-testid="lead-note-save-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add note"}
      </button>
      <ul className="mt-4 space-y-3" data-testid="lead-notes-list">
        {[...(lead.notes || [])].reverse().map((n) => (
          <li key={n.id} className="rounded-xl bg-[#fbf8f3] p-3 text-sm">
            <p className="whitespace-pre-wrap text-[#1f2328]">{n.text}</p>
            <p className="mt-1 text-xs text-neutral-500">{n.by} · {fmtDate(n.at)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const LeadSheet = ({ id, statuses, onClose, onChanged }) => {
  const { user } = useAuth();
  const { data: lead, refetch } = useQuery({
    queryKey: ["lead", id],
    queryFn: () => api.get(`/staff/leads/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 0,
  });

  const setStatus = async (status) => {
    try {
      await api.patch(`/staff/leads/${id}`, { status });
      toast.success(`Marked as ${status}`);
      refetch();
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="lead-detail-sheet">
        {!lead ? (
          <Loader2 className="mt-10 h-6 w-6 animate-spin text-neutral-400" />
        ) : (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="font-display text-2xl" data-testid="lead-detail-name">{lead.contact.name}</SheetTitle>
              <SheetDescription>{lead.contact.residence_country} · received {fmtDate(lead.created_at)}{lead.requirements_edited ? " · edited by visitor" : ""}</SheetDescription>
            </SheetHeader>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <a href={whatsappLink(lead, user.name)} target="_blank" rel="noopener noreferrer" className="col-span-2 flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-white" data-testid="lead-detail-whatsapp-button">
                <MessageCircle className="h-5 w-5" /> WhatsApp {lead.contact.phone_e164}
              </a>
              <a href={`mailto:${lead.contact.email}`} className="col-span-2 flex h-11 items-center justify-center gap-2 rounded-full border border-neutral-300 text-sm font-medium" data-testid="lead-detail-email-button"><Mail className="h-4 w-4" /> Email</a>
            </div>
            <div className="mt-5 text-sm text-neutral-600">
              <p data-testid="lead-detail-email">{lead.contact.email}</p>
              {lead.contact.contact_time && <p>Preferred time: {lead.contact.contact_time}</p>}
              <p className="text-xs font-medium text-[#0f6d06]">Visitor was told: WhatsApp chat only, no calls.</p>
              <p>Updates opt-in: {lead.contact.updates_opt_in ? "Yes" : "No"}</p>
              <p data-testid="lead-detail-confirmation-email">Confirmation email: {lead.confirmation_email || "pending"}</p>
            </div>
            <div className="mt-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500">Status</p>
              <div className="flex flex-wrap gap-2" data-testid="lead-status-options">
                {statuses.map((s) => (
                  <button key={s} onClick={() => setStatus(s)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${lead.status === s ? "border-[#1f2328]" : "border-transparent opacity-70 hover:opacity-100"}`} data-testid={`lead-status-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                    <StatusBadge status={s} />
                  </button>
                ))}
              </div>
            </div>
            <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-2xl border border-neutral-200 p-4 text-sm" data-testid="lead-detail-requirements">
              {ROWS.map(([k, label]) => (
                <div key={k} className="contents">
                  <dt className="text-neutral-500">{label}</dt>
                  <dd className="font-medium">{Array.isArray(lead.answers[k]) ? lead.answers[k].join(", ") : lead.answers[k] || "—"}</dd>
                </div>
              ))}
            </dl>
            {Object.keys(lead.utm || {}).length > 0 && (
              <p className="mt-3 text-xs text-neutral-500">Source: {Object.entries(lead.utm).map(([k, v]) => `${k}=${v}`).join(" · ")}</p>
            )}
            <ShareComposer lead={lead} staffName={user.name} onChanged={() => { refetch(); onChanged(); }} />
            <MatchPanel lead={lead} onChanged={() => { refetch(); onChanged(); }} />
            <Notes lead={lead} onAdded={refetch} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
